import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  Typography,
  Collapse,
} from "@mui/material";
import Divider from "../../../components/Divider";
import MoreVert from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DynamicTable from "../../../components/dynamicTable/DynamicTable";
import {
  DATE_TYPE,
  OptionalProperty,
  TABLE_HEADER_ARRAY,
  TABLE_HEADER_ITEM,
} from "../../../utils/Types";
import { FC, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import exportFromJSON from "export-from-json";
import useGetMasterTableDataQuery from "../hooks/useGetMasterTableDataQuery";
import { MASTER_TABLE_ITEM } from "../types/CommonTypes";
import formatDateTime, { formatTimeString } from "../../../helpers/formatDateTime";
import { API_DATA_LIMIT } from "../../../utils/Constants";
import useFilterValue from "../hooks/useFilterValue";
import { buildQueryStringFromFilters } from "../../../helpers/buildQueryStringFromFilters";
import { useTranslation } from "react-i18next";
import capitalize from "../../../helpers/capitalize";
import useDebounce from "../../../hooks/useDebounce";
import useGetMasterTableColumnQuery from "../hooks/useGetMasterTableColumnQuery";
import { globalStyles } from "../../../styles/GlobalStyles";

type COLUMN_SETTINGS = OptionalProperty<TABLE_HEADER_ITEM, "label">;

const columnSettings: Array<COLUMN_SETTINGS> = [
  //Identity & Context
  { key: "mdName", width: 180 },
  { key: "procedureName", width: 380 },
  { key: "user", width: 180 },
  { key: "otName", width: 100 },
  // Schedule & Status
  {
    key: "fullCaseScheduledStartDateTime",
    type: "timestamp",
  },
  {
    key: "caseScheduleStart",
    type: "timestamp",
    position: "right",
  },
  {
    key: "caseTiming",
    type: "timestamp",
    position: "right",
  },
  { key: "caseRank", position: "right" },
  { key: "caseDelay", position: "right" },
  { key: "endTimeVariance", position: "right" },
  {
    key: "nextPatientCallTimestamp",
    type: "timestamp",
    position: "right",
  },
  {
    key: "skippedAt",
    type: "timestamp",
    position: "right",
  },
  // { key: "milestoneName", label: "Milestone" },
  // { key: "lastTimestamp", label: "Final_Timestamp" },
  // Milestone Timestamps
  {
    key: "roomReadyTimestamp",
    label: "Room_Ready",
    type: "timestamp",
    position: "right",
  },
  {
    key: "wheelsInTimestamp",
    label: "Wheels_In",
    type: "timestamp",
    position: "right",
  },
  {
    key: "timeoutTimestamp",
    label: "Timeout",
    type: "timestamp",
    position: "right",
  },
  {
    key: "anesthesiaStartTimestamp",
    label: "Anesthesia_Start",
    type: "timestamp",
    position: "right",
  },
  {
    key: "patientAsleepTimestamp",
    label: "Patient_Asleep",
    type: "timestamp",
    position: "right",
  },
  {
    key: "patientReadyTimestamp",
    label: "Patient_Ready",
    type: "timestamp",
    position: "right",
  },
  {
    key: "procedureStartTimestamp",
    label: "Procedure_Start",
    type: "timestamp",
    position: "right",
  },
  { key: "procedureEndTimestamp", label: "Procedure_End", type: "timestamp" },
  {
    key: "patientAwakeTimestamp",
    label: "Patient_Awake",
    type: "timestamp",
    position: "right",
  },
  {
    key: "readyToExitTimestamp",
    label: "Ready_To_Exit",
    type: "timestamp",
    position: "right",
  },
  {
    key: "wheelsOutTimestamp",
    label: "Wheels_Out",
    type: "timestamp",
    position: "right",
  },
  {
    key: "roomCleanStartTimestamp",
    label: "Room_Clean_Start",
    type: "timestamp",
    position: "right",
  },
  {
    key: "roomCleanEndTimestamp",
    label: "Room_Clean_End",
    type: "timestamp",
    position: "right",
  },
  // Computed Durations
  { key: "totalCaseTime", position: "right" },
  { key: "turnoverTime", position: "right" },
  {
    key: "surgeonTurnoverTime",
    position: "right",
  },
  {
    key: "durationRoomReadyToWheelsIn",
    position: "right",
  },
  {
    key: "durationWheelsInToPatientReady",
    position: "right",
  },
  // { key: "patientAsleep", label: "Fully_Anesthetised_Timestamp" },
  {
    key: "durationPatientReadyToProcedureStart",
    position: "right",
  },
  {
    key: "durationProcedureStartToProcedureEnd",
    position: "right",
  },
  {
    key: "durationReadyToExitToWheelsOut",
    position: "right",
  },
  // { key: "patientAwake", label: "Awaken_from_Anesthesia_Timestamp" },
  {
    key: "durationWheelsOutToRoomClean",
    position: "right",
  },
  {
    key: "durationRoomCleanStartToEnd",
    position: "right",
  },
  {
    key: "roomSetupTime",
    position: "right",
  },
  // Remaining New Case Metadata
  {
    key: "implantsUsed",
    label: "Implants_Used",
    position: "right",
  },
  {
    key: "woundStatus",
    label: "Wound",
    position: "right",
  },
  {
    key: "remarks",
    position: "right",
  },
];

const MasterDataTable: FC<{ masterDataTableRef?: any }> = ({
  masterDataTableRef,
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [hasUserSelection, setHasUserSelection] = useState(false);

  const { data: masterTableColumnData, isLoading: isLoadingColumn } =
    useGetMasterTableColumnQuery();

  const columns: TABLE_HEADER_ARRAY = useMemo(
    () =>
      masterTableColumnData?.columns?.map((col) => {
        if (col.field === "durationProcedureEndToWheelsOut") {
          return {
            label: "Ready to Exit → Wheels Out",
            ...columnSettings.find(
              (setting) => setting.key === "durationReadyToExitToWheelsOut",
            ),
            key: "durationReadyToExitToWheelsOut",
            visible: col.visible,
            category: col.category,
            alwaysDisplayed: col.alwaysDisplayed,
          };
        }
        return {
          label: col.label,
          ...columnSettings.find((setting) => setting.key === col.field),
          key: col.field,
          visible: col.visible,
          category: col.category,
          alwaysDisplayed: col.alwaysDisplayed,
        };
      }) ?? [],
    [masterTableColumnData?.columns],
  );

  // Group columns by category
  const columnsByCategory = useMemo(() => {
    const grouped: Record<string, TABLE_HEADER_ARRAY> = {};
    columns.forEach((col) => {
      if (col.key !== "caseId") {
        const category = (col as any).category || "Other";
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(col);
      }
    });
    return grouped;
  }, [columns]);

  // Get category selection state
  const getCategoryState = (category: string) => {
    const categoryColumns = columnsByCategory[category] || [];
    const categoryKeys = categoryColumns.map((col) => col.key);
    const selectedCount = categoryKeys.filter(
      (key) =>
        alwaysDisplayedKeys.includes(key) || visibleColumns.includes(key),
    ).length;

    if (selectedCount === 0) return "none";
    if (selectedCount === categoryKeys.length) return "all";
    return "some";
  };

  // Toggle category (select/deselect all columns in category)
  const toggleCategory = (category: string) => {
    setHasUserSelection(true);
    const categoryColumns = columnsByCategory[category] || [];
    const categoryKeys = categoryColumns.map((col) => col.key);
    const categoryState = getCategoryState(category);

    if (categoryState === "all") {
      // Deselect all columns in this category
      setVisibleColumns((prev) =>
        prev.filter(
          (key) =>
            !categoryKeys.includes(key) || alwaysDisplayedKeys.includes(key),
        ),
      );
    } else {
      // Select all columns in this category
      setVisibleColumns((prev) => {
        const newColumns = [...prev];
        categoryKeys
          .filter((key) => !alwaysDisplayedKeys.includes(key))
          .forEach((key) => {
            if (!newColumns.includes(key)) {
              newColumns.push(key);
            }
          });
        return newColumns;
      });
    }
  };

  useEffect(() => {
    if (masterTableColumnData?.columns && !hasUserSelection) {
      // Show columns with alwaysDisplayed=true by default
      const alwaysDisplayedCols = masterTableColumnData.columns
        .filter((col) => col.alwaysDisplayed)
        .map((col) => col.field);

      setVisibleColumns(alwaysDisplayedCols);
    }
  }, [masterTableColumnData?.columns, hasUserSelection]);

  const alwaysDisplayedKeys = useMemo(
    () => columns.filter((c) => c.alwaysDisplayed).map((c) => c.key),
    [columns],
  );
  const filters = useFilterValue();
  const queryString = buildQueryStringFromFilters({
    ...filters,
    selectedColumns: visibleColumns,
  });

  const { data: masterTableData, isLoading: isLoadingTableData } =
    useGetMasterTableDataQuery(currentPage, queryString);

  const { data = [], total = 0 } = masterTableData ?? {};

  const TOGGLE_COLUMN_KEYS = columns.map((col) => col.key);

  const filteredColumns = useMemo(
    () =>
      columns.filter(
        (col) =>
          alwaysDisplayedKeys.includes(col.key) ||
          visibleColumns.includes(col.key),
      ),
    [columns, visibleColumns, alwaysDisplayedKeys],
  );
const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null);

  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchColumn, setSearchColumn] = useState("");
  const debouncedSearchColumn = useDebounce(searchColumn, 300);
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});

  const toggleableKeys = useMemo(
    () => TOGGLE_COLUMN_KEYS.filter((k) => !alwaysDisplayedKeys.includes(k)),
    [TOGGLE_COLUMN_KEYS, alwaysDisplayedKeys],
  );

  const { allToggleableSelected, noneToggleableSelected } = useMemo(() => {
    if (toggleableKeys.length === 0) {
      return { allToggleableSelected: false, noneToggleableSelected: true };
    }
    const all = toggleableKeys.every((k) => visibleColumns.includes(k));
    const none = toggleableKeys.every((k) => !visibleColumns.includes(k));
    return { allToggleableSelected: all, noneToggleableSelected: none };
  }, [toggleableKeys, visibleColumns]);


  // Initialize expanded categories - default to all expanded
  useEffect(() => {
    const categoryKeys = Object.keys(columnsByCategory);
    if (categoryKeys.length > 0) {
      setExpandedCategories((prev) => {
        // Only initialize if not already set
        if (Object.keys(prev).length === 0) {
          const initialExpanded: Record<string, boolean> = {};
          categoryKeys.forEach((category) => {
            initialExpanded[category] = true;
          });
          return initialExpanded;
        }
        // Preserve existing state, but add any new categories
        const updated = { ...prev };
        categoryKeys.forEach((category) => {
          if (!(category in updated)) {
            updated[category] = true;
          }
        });
        return updated;
      });
    }
  }, [columnsByCategory]);

  const toggleCategoryExpand = (category: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

 const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
  e.preventDefault();
  e.stopPropagation();

  setAnchorPosition({ top: e.clientY, left: e.clientX }); // frozen position
  setMenuOpen(true);
};


 const handleMenuClose = () => {
  setMenuOpen(false);
  setAnchorPosition(null);
  setSearchColumn("");
};


  const toggleColumn = (key: string) => {
    setHasUserSelection(true);
    if (alwaysDisplayedKeys.includes(key)) {
      return;
    }
    setVisibleColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const toggleSelectAll = () => {
    setHasUserSelection(true);

    setVisibleColumns((prev) => {
      if (allToggleableSelected) {
        // deselect only toggleable columns
        return prev.filter((k) => !toggleableKeys.includes(k));
      }

      // select all toggleable columns (preserve existing)
      return Array.from(new Set([...prev, ...toggleableKeys]));
    });
  };

  // Filter and group columns by category for menu display
  const filteredColumnsByCategory = useMemo(() => {
    const filtered: Record<string, TABLE_HEADER_ARRAY> = {};
    Object.keys(columnsByCategory).forEach((category) => {
      const categoryCols = columnsByCategory[category].filter((col) =>
        col.label?.toLowerCase().includes(debouncedSearchColumn.toLowerCase()),
      );
      if (categoryCols.length > 0) {
        filtered[category] = categoryCols;
      }
    });
    return filtered;
  }, [columnsByCategory, debouncedSearchColumn]);

  const masterTableList = data.map((item: MASTER_TABLE_ITEM, index: number) => {
    let implantsUsed: string;
    if (item.implantsUsed === true) {
      implantsUsed = "Yes";
    } else if (item.implantsUsed === false) {
      implantsUsed = "No";
    } else {
      implantsUsed = "--";
    }

    let antibioticUsed: string;
    if (item.antibioticUsed === true) {
      antibioticUsed = "Yes";
    } else if (item.antibioticUsed === false) {
      antibioticUsed = "No";
    } else {
      antibioticUsed = "--";
    }

    let inconsistentTimestamps: string;
    if (item.inconsistentTimestamps === true) {
      inconsistentTimestamps = "Yes";
    } else if (item.inconsistentTimestamps === false) {
      inconsistentTimestamps = "No";
    } else {
      inconsistentTimestamps = "--";
    }

    let milestonesSkipped: string;
    if (item.milestonesSkipped === true) {
      milestonesSkipped = "Yes";
    } else if (item.milestonesSkipped === false) {
      milestonesSkipped = "No";
    } else {
      milestonesSkipped = "--";
    }

    let durationReadyToExitToWheelsOut: number | null = null;
    if (item.readyToExitTimestamp && item.wheelsOutTimestamp) {
      const readyToExitTime = new Date(
        item.readyToExitTimestamp as any,
      ).getTime();
      const wheelsOutTime = new Date(
        item.wheelsOutTimestamp as any,
      ).getTime();
      durationReadyToExitToWheelsOut = Math.round(
        (wheelsOutTime - readyToExitTime) / 60000,
      );
    }

    return {
      ...item,
      sn: (currentPage - 1) * API_DATA_LIMIT + index + 1,
      implantsUsed,
      antibioticUsed,
      inconsistentTimestamps,
      milestonesSkipped,
      procedureNature: capitalize(item.procedureNature),
      durationReadyToExitToWheelsOut,
      antiobioticAdminTime: formatTimeString(item.antiobioticAdminTime),
    };
  });


  //  for export csv with selected items

  const isValidDateInput = (value: unknown): value is DATE_TYPE =>
    typeof value === "string" ||
    typeof value === "number" ||
    value instanceof Date;

  const buildExportData = useCallback(
    (
      rows: MASTER_TABLE_ITEM[],
      visibleHeaders: TABLE_HEADER_ARRAY,
    ): Record<string, string | number>[] => {
      return rows.map((row) => {
        const transformedRow: Record<string, string | number> = {};

        visibleHeaders.forEach(({ key, label, type }) => {
          let value = row[key as keyof MASTER_TABLE_ITEM];

          if (type === "timestamp" && isValidDateInput(value)) {
            value = formatDateTime(value);
          }

          if (key === "procedureNature") {
            value = capitalize(value as string);
          }

          const exportLabel = t(label);

          transformedRow[exportLabel] =
            value !== undefined && value !== null && value !== ""
              ? String(value)
              : "--";
        });

        return transformedRow;
      });
    },
    [t],
  );

  const [selectedRows, setSelectedRows] = useState<MASTER_TABLE_ITEM[]>([]);
  useImperativeHandle(
    masterDataTableRef,
    () => ({
      exportMasterDataTable() {
        const rowsToExport =
          selectedRows.length > 0 ? selectedRows : masterTableList;

        if (!rowsToExport.length) {
          alert("No data available to export");
          return;
        }

        const exportData = buildExportData(rowsToExport, filteredColumns);

        exportFromJSON({
          data: exportData,
          fileName: "orva-master-data",
          exportType: "csv",
        });
      },
    }),
    [selectedRows, masterTableList, filteredColumns, buildExportData],
  );

  const renderMenuButton = (
    <IconButton
      ref={menuButtonRef}
      onClick={handleMenuOpen}
      onMouseDown={(e) => e.stopPropagation()}
      sx={{
        width: 36,
        height: 36,
        borderRadius: 2,
        backgroundColor: "rgba(0,0,0,0.04)",
        "&:hover": {
          backgroundColor: "rgba(0,0,0,0.08)",
        },
      }}
    >
      <MoreVert />
    </IconButton>
  );

  return (
    <Box sx={globalStyles.colFlex1} pt={2}>
      <DynamicTable
        isLoading={isLoadingTableData || isLoadingColumn}
        headers={filteredColumns}
        rowData={masterTableList}
        enablePagination
        totalPage={Math.ceil(total / API_DATA_LIMIT)}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        enableRowSelection
        renderHeaderMenu={() => renderMenuButton}
        onSelectionChange={(rows) => setSelectedRows(rows)}
      />

      {/* Selection Menu */}
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition ?? undefined}
        open={menuOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            handleMenuClose();
          }
        }}
        disableAutoFocusItem
        disableEnforceFocus
        disableRestoreFocus
        keepMounted
        disablePortal
        MenuListProps={{
          onMouseDown: (e) => {
            // IMPORTANT: avoid preventDefault here (can cause weird focus/clickaway behavior)
            e.stopPropagation();
          },
        }}
        slotProps={{
          paper: {
            onMouseDown: (e: any) => e.stopPropagation(),
            onClick: (e: any) => e.stopPropagation(),
            sx: {
              width: 320,
              maxHeight: 480,
              borderRadius: 2,
              overflow: "hidden",
            },
          },
        }}
      >
        <MenuItem
          disableRipple
          onClick={(e) => e.stopPropagation()}
          sx={{
            p: 1,
            position: "sticky",
            top: 0,
            zIndex: 2,
            backgroundColor: "background.paper",
            "&:hover": {
              backgroundColor: "background.paper",
            },
          }}
        >
          <TextField
            fullWidth
            placeholder="Search"
            value={searchColumn}
            onChange={(e) => setSearchColumn(e.target.value)}
            variant="outlined"
            size="small"
            slotProps={{
              input: {
                startAdornment: (
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: "text.secondary", mr: 1 }}
                  />
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F3F4F6",
                borderRadius: "10px",
                height: 40,
                paddingLeft: 1,
                "& fieldset": {
                  border: "none",
                },
                "&:hover fieldset": {
                  border: "none",
                },
                "&.Mui-focused fieldset": {
                  border: "none",
                },
              },
              "& input::placeholder": {
                color: "#6B7280",
                opacity: 1,
              },
            }}
          />
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            toggleSelectAll();
          }}
          sx={{
            position: "sticky",
            top: 56,
            zIndex: 2,
            backgroundColor: "background.paper",
          }}
        >
          <Checkbox
            checked={allToggleableSelected}
            indeterminate={!allToggleableSelected && !noneToggleableSelected}
            sx={{
              "&.Mui-checked": {
                color: "custom.background.brand",
              },
            }}
          />
          <ListItemText primary={t("Select_All")} />
        </MenuItem>

        <Box
          sx={{
            maxHeight: 300,
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              width: 0,
              height: 0,
            },
            "&:hover": {
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                width: 6,
              },
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.25)",
              borderRadius: 4,
            },
          }}
        >
          {Object.keys(filteredColumnsByCategory).map((category) => {
            const categoryColumns = filteredColumnsByCategory[category];
            const categoryState = getCategoryState(category);
            const isCategoryChecked = categoryState === "all";
            const isCategoryIndeterminate = categoryState === "some";

            const isExpanded = expandedCategories[category] ?? true;

            return (
              <Box key={category}>
                {/* Category Header with Checkbox and Expand/Collapse */}
                <MenuItem
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    backgroundColor: "rgba(0,0,0,0.02)",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.05)",
                    },
                  }}
                >
                  <Checkbox
                    checked={isCategoryChecked}
                    indeterminate={isCategoryIndeterminate}
                    onClick={(e) => {
                      
                      e.stopPropagation();
                      toggleCategory(category);
                    }}
                    sx={{
                      "&.Mui-checked": {
                        color: "custom.background.brand",
                      },
                      "&.MuiCheckbox-indeterminate": {
                        color: "custom.background.brand",
                      },
                    }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ flex: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCategory(category);
                    }}
                  >
                    {category}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCategoryExpand(category);
                    }}
                    sx={{
                      padding: 0.5,
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {isExpanded ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                </MenuItem>

                {/* Columns under this category */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  {categoryColumns.map((col) => (
                    <MenuItem
                      key={col.key}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleColumn(col.key);
                      }}
                      sx={{
                        pl: 4,
                      }}
                    >
                      <Checkbox
                        checked={visibleColumns.includes(col.key)}
                        disabled={col.alwaysDisplayed}
                        sx={{
                          "&.Mui-checked": {
                            color: "custom.background.brand",
                          },
                        }}
                      />
                      <ListItemText primary={t(col.label)} />
                    </MenuItem>
                  ))}
                </Collapse>

                {/* Divider between categories */}
                {Object.keys(filteredColumnsByCategory).indexOf(category) <
                  Object.keys(filteredColumnsByCategory).length - 1 && (
                  <Divider sx={{ my: 0.5 }} />
                )}
              </Box>
            );
          })}
        </Box>
      </Menu>
    </Box>
  );
};

export default MasterDataTable;
